import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';


import { feynmanSpeech, feynmanSvg, readReaction } from './feynman-diagram';
import { mathAnnouncement, reactionLabel } from './formula-announcement';
import { renderMathFormula } from './math-formula-extension';
import { composeReaction } from './reaction-compose';
import { displayText } from './reaction-doc';
import { ReactionFormulaView } from './reaction-formula-view';
import { noteSpeech } from './reaction-quantities';
import { reactionSpeech } from './reaction-speech';

import i18n from '@/lib/i18n';

export interface ReactionFormulaOptions {
  onEditRequest: (
    formula: string,
    note: string,
    /** The mediator, which doubles as the Feynman flag. Empty draws the equation. */
    feynman: string,
    update: (next: { formula: string; note: string; feynman: string }) => void,
  ) => void;
}

/**
 * WHAT A REACTION LOOKS LIKE AND WHAT IT IS CALLED — the one place that decides.
 *
 * There are two render paths: the node view a teacher is looking at while editing, and
 * `processReactionHTML`, which turns stored HTML into what a student sees. They have drifted
 * apart twice now. The enthalpy was taught to one of them and appeared in the editor and not on
 * the page; the Feynman flag was taught to the other and drew on the page while the editor went
 * on typesetting the equation — the owner's report was "it seems to work, but i don't see
 * anything drawn".
 *
 * So neither path decides anything any more. They call this.
 */
export function reactionBody(formula: string, note: string, feynman: string): {
  html: string;
  error: string | null;
  /** The chemistry library has not landed; what is on screen must not be overwritten yet. */
  pending: boolean;
  label: string;
} {
  const { html: rendered, error, pending } = composeReaction(formula);
  const noteHtml = note ? renderMathFormula(note).html : '';

  /* Flagged, the equation is DRAWN instead of typeset — and the drawing replaces it rather than
     joining it, because an incorrect diagram is a multiple-choice distractor and printing the
     equation beside it would answer the question. */
  const reading = feynman ? readReaction(formula) : null;
  const diagram = reading ? feynmanSvg(reading, feynman, displayText) : null;
  const body = diagram ?? rendered;

  const spoken = reactionLabel(formula);
  const said = reading && feynman
    ? feynmanSpeech(reading, feynman, (k) => i18n.t(k), (p) => reactionSpeech(p) || p) ?? spoken
    : spoken;
  /* A note built from the fields is SAID — "delta H, equals, minus, 92, kilojoules per mole" —
     rather than spelled. One typed by hand still goes through the announcer. */
  const saidNote = note
    ? noteSpeech(note, (k) => i18n.t(k), mathAnnouncement) ?? mathAnnouncement(note)
    : '';

  return {
    html: noteHtml ? `${body}<span class="reaction-note">${noteHtml}</span>` : body,
    error,
    pending,
    label: said && note ? `${said}, ${saidNote}` : said,
  };
}

export function processReactionHTML(html: string): string {
  if (!html.includes('data-reaction-formula')) return html;
  const div = document.createElement('div');
  div.innerHTML = html;
  div.querySelectorAll<HTMLElement>('[data-reaction-formula]').forEach((el) => {
    const formula = el.getAttribute('data-reaction-formula') ?? '';
    if (!formula) return;
    const { html: body, pending, label } = reactionBody(
      formula,
      el.getAttribute('data-reaction-note') ?? '',
      el.getAttribute('data-reaction-feynman') ?? '',
    );
    /*
     * NEVER downgrade. This runs on every keystroke and its output is what gets stored, so a
     * structure whose library has not loaded yet must not overwrite the drawing already sitting
     * in the element — that would save the placeholder over the molecule. Leaving the element
     * alone costs nothing: the content is already correct, and `prepareChemistry` will have the
     * editor re-render once the library lands.
     */
    if (pending) return;
    if (body) el.innerHTML = body;

    /**
     * The accessible name, so a screen reader is handed the SENTENCE and never the markup.
     *
     * KaTeX emits presentation MathML — layout, not meaning — so a reader applies maths rules to
     * a reaction and H₂O arrives as "H sub 2 O", arrows as "rightwards arrow" or nothing at all.
     * `role='img'` makes the node atomic with this name, so the glyphs underneath are not
     * announced after it — and because that role then has a reader say "graphic", the name
     * itself leads with "Reaction:". See `reactionLabel`, which owns that wording.
     *
     * Written HERE, at render, rather than in the node's `renderHTML`. Baked into the node it
     * would be serialised into stored content and go stale the moment either the formula or this
     * derivation changed; derived on display it is always current and self-heals old content —
     * the same reason `remapInkColors` works the way it does.
     */
    if (label) {
      el.setAttribute('role', 'img');
      el.setAttribute('aria-label', label);
    }
  });
  return div.innerHTML;
}

const reactionAttrs = (el: HTMLElement) => ({
  formula: el.getAttribute('data-reaction-formula') ?? '',
  note: el.getAttribute('data-reaction-note') ?? '',
  feynman: el.getAttribute('data-reaction-feynman') ?? '',
});

export const ReactionFormula = Node.create<ReactionFormulaOptions>({
  name: 'reactionFormula',
  inline: true,
  group: 'inline',
  atom: true,
  selectable: true,
  draggable: false,

  addOptions() {
    return { onEditRequest: () => {} };
  },

  addAttributes() {
    /*
     * `note` is the enthalpy, or whatever else belongs BESIDE the equation rather than in it.
     *
     * It is an attribute of the same node so the two move as one object: written as prose after
     * the insert it would drift away from its equation the first time the text reflowed. And it
     * is held apart from the formula because it is not chemistry — mhchem reads a sign after a
     * caret as a charge, so `kJ mol^-1` renders as mol¹⁻ and `10^-3` as 10³⁻. The view sends it
     * to the MATHS renderer, which has no such rule.
     */
    return {
      formula: { default: '' },
      /* `renderHTML: () => ({})` keeps it out of the serialised attributes: `data-reaction-note`
         is what `parseHTML` reads, and a bare `note="…"` beside it is noise in stored content.
         (`formula` leaks the same way and is left alone — changing it would rewrite every
         reaction already saved.) */
      note: { default: '', renderHTML: () => ({}) },
      /* The mediator, which doubles as the flag: empty means draw it as an equation. */
      feynman: { default: '', renderHTML: () => ({}) },
    };
  },

  parseHTML() {
    return [
      { tag: 'span[data-reaction-formula]', getAttrs: (el) => reactionAttrs(el as HTMLElement) },
      { tag: 'div[data-reaction-formula]',  getAttrs: (el) => reactionAttrs(el as HTMLElement) },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(
        {
          'data-reaction-formula': node.attrs.formula,
          ...(node.attrs.note ? { 'data-reaction-note': node.attrs.note } : {}),
          ...(node.attrs.feynman ? { 'data-reaction-feynman': node.attrs.feynman } : {}),
          contenteditable: 'false', class: 'reaction-formula',
        },
        HTMLAttributes,
      ),
      String(node.attrs.formula),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ReactionFormulaView);
  },
});
